import { yupResolver } from "@hookform/resolvers/yup";
import { Button, ButtonVariant } from "@src/components/button/Button";
import { filtersEmptyValues } from "@src/components/utils/hookFormUtils";
import { getEventOptions, getGlobalInventoryOptions } from "@src/components/utils/reactSelectUtils";
import {
	decodeSmartSearchValue,
	encodeSmartSearchValue,
	getSmartSearchOptions,
	isSmartSearchOption,
} from "@src/components/utils/smartSearchUtils";
import { IGlobalInventoryRawItem } from "@src/store/pricing/queryGlobalInventorySlice";
import { eventActions, settingsActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { HeaderFiltersProps, HeaderFiltersValidation } from "@src/validation/HeaderFiltersValidation";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { PricingTabEnum } from "@src/store/pricing/selectionSlice";
import { PricingFilters } from "./PricingFilters";
import styles from "@src/components/filtersPanel/FilterLayout.module.scss";
import { MixpanelCategory, mixpanelTrack } from "@src/components/utils/mixPanelUtils";

const getSmartSearchFormValue = (params: URLSearchParams) => {
	const smartSearch = params.get("smartSearch");
	const smartSearchField = params.get("smartSearchField");
	if (smartSearch && smartSearchField) return encodeSmartSearchValue(smartSearchField, smartSearch);
	return smartSearch ?? params.get("searchString") ?? "";
};

export const PricingHeader: React.FC = () => {
	// Add later if needed
	// const isLightTheme = useAppSelector((state) => state.settings.theme) === Theme.Light;
	const [params, setParams] = useSearchParams();
	const companyName = useAppSelector((state) => state.settings.companyName);
	const dispatch = useAppDispatch();

	const setFormValues = useCallback(
		(params: URLSearchParams) => ({
			companyName: companyName ?? "",
			eventName: params.get("eventName") ?? "",
			eventHeadliner: params.get("eventHeadliner") ?? "",
			venue: params.get("venue") ?? "",
			startDate: params.get("startDate") ?? "",
			endDate: params.get("endDate") ?? "",
			smartSearch: getSmartSearchFormValue(params),
			tags: params.get("tags") ?? "",
			inventoryTags: params.get("inventoryTags") ?? "",
			category: params.get("category") ?? "",
			section: params.get("section") ?? "",
			row: params.get("row") ?? "",
			minQuantity: params.get("minQuantity") ?? "",
			daysSinceLastUpdate: params.get("daysSinceLastUpdate") ?? "",
			listingId: params.get("listingId") ?? "",
			eventId: params.get("eventId") ?? "",
			poId: params.get("poId") ?? "",
			poStartDate: params.get("poStartDate") ?? "",
			poEndDate: params.get("poEndDate") ?? "",
		}),
		[companyName],
	);

	const savedFilterLS = JSON.parse(localStorage.portal_saved_filters ?? null);
	const [savedFilters, setSavedFilters] = useState(savedFilterLS);

	const { control, handleSubmit, reset, setValue, getValues } = useForm({
		mode: "onChange",
		resolver: yupResolver(HeaderFiltersValidation),
		defaultValues: setFormValues(params),
	});

	const onFiltersSave = () => {
		const values = getValues();
		if (!savedFilters) {
			setSavedFilters([values]);
			localStorage.setItem("portal_saved_filters", JSON.stringify([values]));
			return;
		}
		const combinedFilters = [...savedFilters, values];

		setSavedFilters(combinedFilters);
		localStorage.setItem("portal_saved_filters", JSON.stringify(combinedFilters));
		mixpanelTrack(MixpanelCategory.SAVED_FILTERS);
	};

	useEffect(() => {
		if (!companyName) return;
		setValue("companyName", companyName);
	}, [companyName, setValue]);

	useEffect(() => {
		reset(setFormValues(params));
	}, [params, reset, setFormValues]);

	const onSubmit: SubmitHandler<HeaderFiltersProps> = (data) => {
		const { smartSearch, ...rest } = data;
		Object.entries(rest).forEach(([k, v]) => {
			v ? params.set(k, String(v)) : params.delete(k);
		});

		const { term, fieldLabel } = decodeSmartSearchValue(smartSearch?.trim() ?? "");
		const smartSearchTerm = term.trim();
		params.delete("smartSearch");
		params.delete("smartSearchField");
		params.delete("searchString");
		if (smartSearchTerm && fieldLabel) {
			params.set("smartSearch", smartSearchTerm);
			params.set("smartSearchField", fieldLabel);
		} else if (smartSearchTerm) {
			const isFreeText = !isSmartSearchOption(smartSearchOptions, smartSearchTerm);
			params.set(isFreeText ? "searchString" : "smartSearch", smartSearchTerm);
		}
		params.delete("eventsPage");
		const companyNameQuery = params.get("companyName") ?? "";
		const hasCompanyChanged = companyName !== companyNameQuery;

		if (hasCompanyChanged) {
			dispatch(eventActions.setEvent({ event: null }));
			dispatch(settingsActions.setCompanyName(companyNameQuery));
		}

		params.delete("companyName");
		setParams(params);
	};

	const events = useAppSelector((state) => state.pricing.events.data.Events);
	const globalInventory = useAppSelector((state) => state.pricing.globalInventory.data as Array<IGlobalInventoryRawItem>);
	const pricingTab = useAppSelector((state) => state.pricing.selection.pricingTab);
	const isInventoryTab = pricingTab === PricingTabEnum.GlobalInventory;

	const categoryOptions = isInventoryTab
		? getGlobalInventoryOptions(globalInventory, "Cat_Desc")
		: getEventOptions(events, "Cat_Desc");
	const headlinerOptions = isInventoryTab
		? getGlobalInventoryOptions(globalInventory, "Event_Headliner")
		: getEventOptions(events, "Event_Headliner");
	const venueOptions = isInventoryTab
		? getGlobalInventoryOptions(globalInventory, "Venue_Name")
		: getEventOptions(events, "Venue_Name");

	venueOptions.sort((a, b) => a.label.localeCompare(b.label));
	if (isInventoryTab) {
		headlinerOptions.sort((a, b) => a.label.localeCompare(b.label));
		categoryOptions.sort((a, b) => a.label.localeCompare(b.label));
	}

	const smartSearchOptions = useMemo(
		() => getSmartSearchOptions<object>(isInventoryTab ? globalInventory : events),
		[isInventoryTab, globalInventory, events],
	);

	const isAdmin = useAppSelector((state) => state.settings.isAdmin);

	const { data, loading } = useAppSelector((state) => state.pricing.companies);
	const companyOptions = data.map((x) => ({
		label: x,
		value: x,
	}));

	const pricingFilterProps = {
		control,
		reset,
		setValue,
		getValues,
		categoryOptions,
		headlinerOptions,
		venueOptions,
		savedFilters,
		smartSearchOptions,
		companyOptions,
		companiesLoading: loading,
		isAdmin,
		onSaveFilters: onFiltersSave,
	};

	const allQueryNames = Object.entries(getValues())
		.map(([k, _]) => k)
		.concat("showSold", "showParking", "showCanceled", "searchString", "smartSearchField");

	const handleResetFilters = () => {
		allQueryNames.forEach((key) => params.delete(key));
		params.delete("eventsPage");
		setParams(params);
		reset(filtersEmptyValues);
	};

	return (
		<form className={styles.panelForm} onSubmit={handleSubmit(onSubmit)}>
			<div className={styles.panelBody}>
				<PricingFilters {...pricingFilterProps} />
			</div>
			<div className={styles.panelFooter}>
				<Button type="button" variant={ButtonVariant.Secondary} onClick={handleResetFilters}>
					Reset filters
				</Button>
				<Button type="submit">Search</Button>
			</div>
		</form>
	);
};
